import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Megaphone, Loader2, LogOut, ShieldCheck, Clock, CheckCircle2, XCircle, Copy, BadgeCheck, Link2, QrCode, Download, TrendingUp } from "lucide-react";
import { applyMpn, login, fetchMpnMe, getUser, isMpn, clearToken, authHeader } from "../lib/auth";
import { API } from "../lib/adventureApi";

const copyText = (t, msg = "Copied") => {
  const ok = () => toast.success(msg);
  if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(t).then(ok).catch(() => fallbackCopy(t, ok)); }
  else fallbackCopy(t, ok);
};
function fallbackCopy(t, ok) {
  try { const ta = document.createElement("textarea"); ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); ok(); }
  catch { toast.error("Couldn't copy — " + t); }
}

function QRModal({ url, title, onClose }) {
  const [img, setImg] = useState(null);
  useEffect(() => { QRCode.toDataURL(url, { width: 480, margin: 2, color: { dark: "#171717", light: "#ffffff" } }).then(setImg).catch(() => {}); }, [url]);
  return (
    <div className="fixed inset-0 z-[1200] bg-black/70 flex items-center justify-center p-6" onClick={onClose} data-testid="qr-modal">
      <div className="bg-white rounded-xl p-6 max-w-xs w-full text-center" onClick={(e) => e.stopPropagation()}>
        <p className="font-display font-bold text-ink mb-3">{title}</p>
        {img ? <img src={img} alt="QR code" className="w-full rounded-lg" data-testid="qr-image" /> : <Loader2 className="w-6 h-6 animate-spin text-terracotta mx-auto" />}
        {img && <a href={img} download={`${title}-qr.png`} data-testid="qr-download" className="mt-4 inline-flex items-center gap-2 bg-ink text-white h-10 px-4 rounded-sm text-sm font-semibold"><Download className="w-4 h-4" /> Download QR</a>}
      </div>
    </div>
  );
}

function CampaignLibrary() {
  const [items, setItems] = useState(null);
  const [qr, setQr] = useState(null);
  useEffect(() => { fetch(`${API}/mpn/campaigns`, { headers: authHeader() }).then((r) => r.json()).then(setItems).catch(() => setItems([])); }, []);
  const fullLink = (path) => `${window.location.origin}${path}`;

  return (
    <div data-testid="mpn-campaign-library">
      <h3 className="font-display font-extrabold text-lg mb-3">Campaign library</h3>
      {items == null ? <div className="text-ink-secondary flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading campaigns…</div>
        : items.length === 0 ? <p className="text-sm text-ink-secondary">No active campaigns yet — check back soon.</p>
        : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((c) => (
            <div key={c.id} className="rounded-xl bg-white border border-sand-dark p-4" data-testid={`campaign-card-${c.code}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-display font-bold text-ink">{c.name}</p>
                <code className="text-[10px] font-mono bg-sand px-1.5 py-0.5 rounded text-terracotta">{c.code}</code>
              </div>
              <p className="text-sm text-ink-secondary mt-0.5">{c.destination} · from A${Number(c.from_price).toLocaleString()} pp</p>
              {c.ad_end && <p className="text-[11px] text-ink-muted mt-1">Offer valid until {c.ad_end}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => copyText(fullLink(c.landing_path), "Tracked link copied")} data-testid={`campaign-copy-${c.code}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-terracotta text-white text-xs font-semibold hover:bg-terracotta-hover transition-colors"><Link2 className="w-3.5 h-3.5" /> Copy tracked link</button>
                <button onClick={() => setQr({ url: fullLink(c.landing_path), title: c.code })} data-testid={`campaign-qr-${c.code}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-sand-dark text-ink text-xs font-semibold hover:border-moss transition-colors"><QrCode className="w-3.5 h-3.5" /> QR code</button>
              </div>
              {c.caption && <p className="text-xs text-ink-muted mt-3 border-t border-sand pt-2 italic">"{c.caption}"</p>}
            </div>
          ))}
        </div>
      )}
      {qr && <QRModal url={qr.url} title={qr.title} onClose={() => setQr(null)} />}
    </div>
  );
}

const COMM_STATUS_CLS = {
  secured: "bg-amber-100 text-amber-800", payable: "bg-blue-100 text-blue-800",
  paid: "bg-moss-light text-moss", on_hold: "bg-ink/10 text-ink-secondary",
  reversed: "bg-terracotta/15 text-terracotta", cancelled: "bg-ink/10 text-ink-muted", estimated: "bg-ink/10 text-ink-secondary",
};

function CommissionCentre({ partner }) {
  const [data, setData] = useState(null);
  const [payout, setPayout] = useState({ payout_info: "", tax_info: "" });
  const [saving, setSaving] = useState(false);
  const load = () => fetch(`${API}/mpn/commissions`, { headers: authHeader() }).then((r) => r.json()).then((d) => { setData(d); setPayout({ payout_info: d.payout_info || "", tax_info: d.tax_info || "" }); }).catch(() => setData({ commissions: [], totals: {} }));
  useEffect(() => { load(); }, []);

  const savePayout = async () => {
    setSaving(true);
    try { const r = await fetch(`${API}/mpn/payout`, { method: "PUT", headers: { ...authHeader(), "Content-Type": "application/json" }, body: JSON.stringify(payout) }); if (!r.ok) throw new Error(); toast.success("Payout details saved"); }
    catch { toast.error("Couldn't save"); } finally { setSaving(false); }
  };

  const downloadCsv = () => {
    const rows = [["Ref", "Campaign", "Tranche", "Basis", "Base", "Rate%", "Amount", "Status", "Created"]];
    (data?.commissions || []).forEach((c) => rows.push([c.booking_ref, c.campaign_code, c.tranche, c.basis, c.base_amount, c.rate, c.amount, c.status, (c.created_at || "").slice(0, 10)]));
    const csv = rows.map((r) => r.map((x) => `"${x ?? ""}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${partner.partner_id}-commissions.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const t = data?.totals || {};
  return (
    <div data-testid="mpn-commission-centre">
      <h3 className="font-display font-extrabold text-lg mb-3">Commission centre</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[["secured", "Secured"], ["payable", "Payable"], ["paid", "Paid"], ["reversed", "Reversed"]].map(([k, label]) => (
          <div key={k} className="rounded-xl bg-white border border-sand-dark p-4" data-testid={`comm-total-${k}`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</p>
            <p className="font-display font-extrabold text-xl text-ink mt-1 tabular-nums">A${Number(t[k] || 0).toLocaleString()}</p>
          </div>
        ))}
      </div>

      {data == null ? <div className="text-ink-secondary flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : data.commissions.length === 0 ? <p className="text-sm text-ink-secondary">No commissions yet — they appear here when your leads pay their deposit and balance.</p>
        : (
        <>
          <div className="flex justify-end mb-2"><button onClick={downloadCsv} data-testid="comm-csv" className="text-sm text-moss font-semibold inline-flex items-center gap-1.5"><Download className="w-4 h-4" /> Download statement (CSV)</button></div>
          <div className="overflow-x-auto rounded-xl border border-sand-dark bg-white">
            <table className="w-full text-sm" data-testid="commission-table">
              <thead className="bg-sand text-ink-secondary font-mono text-[10px] uppercase tracking-widest"><tr>
                <th className="text-left px-3 py-2">Booking</th><th className="text-left px-3 py-2">Campaign</th><th className="text-left px-3 py-2">Tranche</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Status</th></tr></thead>
              <tbody>
                {data.commissions.map((c) => (
                  <tr key={c.id} className="border-t border-sand" data-testid={`comm-row-${c.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{c.booking_ref}</td>
                    <td className="px-3 py-2 text-ink-secondary">{c.campaign_code}</td>
                    <td className="px-3 py-2">T{c.tranche} · {c.basis}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-terracotta">A${Number(c.amount).toLocaleString()}</td>
                    <td className="px-3 py-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${COMM_STATUS_CLS[c.status] || COMM_STATUS_CLS.estimated}`}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="mt-5 rounded-xl bg-white border border-sand-dark p-5" data-testid="payout-form">
        <p className="font-display font-bold mb-3">Payout & tax details</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Payout details</label><input data-testid="payout-info" className={field} value={payout.payout_info} onChange={(e) => setPayout((p) => ({ ...p, payout_info: e.target.value }))} placeholder="Bank / PayPal" /></div>
          <div><label className={lbl}>Tax info</label><input data-testid="payout-tax" className={field} value={payout.tax_info} onChange={(e) => setPayout((p) => ({ ...p, tax_info: e.target.value }))} placeholder="ABN / Tax ID" /></div>
        </div>
        <button onClick={savePayout} disabled={saving} data-testid="payout-save" className="mt-3 h-10 px-5 rounded-sm bg-ink text-white text-sm font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save payout details"}</button>
      </div>
    </div>
  );
}
const LEAD_STATUS_LABEL = {
  lead_registered: "Registered", itinerary_started: "Building", itinerary_completed: "Itinerary ready",
  seven_day_hold: "7-day hold", deposit_pending: "Deposit pending", deposit_paid: "Deposit paid",
  booking_confirmed: "Confirmed", travel_completed: "Travelled", cancelled_refunded: "Cancelled",
};

function LeadRegister() {
  const [leads, setLeads] = useState(null);
  useEffect(() => { fetch(`${API}/mpn/leads`, { headers: authHeader() }).then((r) => r.json()).then(setLeads).catch(() => setLeads([])); }, []);
  const totalCommission = leads ? leads.reduce((a, l) => a + (l.potential_commission || 0), 0) : 0;

  return (
    <div data-testid="mpn-lead-register">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-extrabold text-lg">Your leads</h3>
        {leads && leads.length > 0 && <span className="text-sm text-moss font-semibold flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> A${totalCommission.toLocaleString()} potential commission</span>}
      </div>
      {leads == null ? <div className="text-ink-secondary flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading leads…</div>
        : leads.length === 0 ? <p className="text-sm text-ink-secondary">No leads yet. Share your tracked links to start earning.</p>
        : (
        <div className="overflow-x-auto rounded-xl border border-sand-dark bg-white">
          <table className="w-full text-sm" data-testid="lead-table">
            <thead className="bg-sand text-ink-secondary font-mono text-[10px] uppercase tracking-widest">
              <tr><th className="text-left px-3 py-2">Ref</th><th className="text-left px-3 py-2">Campaign</th><th className="text-left px-3 py-2">Trip</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Potential value</th><th className="text-right px-3 py-2">Commission</th></tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.ref} className="border-t border-sand" data-testid={`lead-row-${l.ref}`}>
                  <td className="px-3 py-2 font-mono text-xs">{l.ref}</td>
                  <td className="px-3 py-2 text-ink-secondary">{l.campaign_code}</td>
                  <td className="px-3 py-2">{l.destination || "—"}</td>
                  <td className="px-3 py-2"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-moss-light text-moss">{LEAD_STATUS_LABEL[l.status] || l.status}</span></td>
                  <td className="px-3 py-2 text-right tabular-nums">A${Number(l.potential_value).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-terracotta">A${Number(l.potential_commission).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const field = "h-11 w-full rounded-sm bg-white border border-sand-dark text-ink text-sm px-3 focus:outline-none focus:ring-2 focus:ring-terracotta";
const lbl = "font-mono text-[11px] uppercase tracking-widest text-ink-secondary mb-1.5 block";

const STATUS_UI = {
  application_received: { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", title: "Application received", msg: "Thanks for applying! Our team is reviewing your application — we'll be in touch soon." },
  under_review: { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", title: "Under review", msg: "Your application is being reviewed. You'll get access to campaigns once approved." },
  approved: { icon: CheckCircle2, color: "text-moss", bg: "bg-moss-light", title: "Approved!", msg: "You're approved — your partner workspace is being activated." },
  active: { icon: BadgeCheck, color: "text-moss", bg: "bg-moss-light", title: "You're an active Marketing Partner", msg: "Your campaign library and tracked links are on the way." },
  suspended: { icon: XCircle, color: "text-terracotta", bg: "bg-terracotta/10", title: "Account suspended", msg: "Your partner account is currently suspended. Please contact Responsible Adventures." },
  terminated: { icon: XCircle, color: "text-terracotta", bg: "bg-terracotta/10", title: "Account terminated", msg: "This partner account has been terminated." },
};

function Dashboard({ partner, onLogout }) {
  const s = STATUS_UI[partner.status] || STATUS_UI.application_received;
  const Icon = s.icon;
  const active = partner.status === "active";
  return (
    <div data-testid="mpn-dashboard" className="max-w-3xl mx-auto px-5 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-terracotta rounded-sm flex items-center justify-center"><Megaphone className="w-5 h-5 text-white" /></div>
          <div>
            <h1 className="font-display font-extrabold text-lg tracking-tight leading-none">Marketing Partner</h1>
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-0.5">Responsible Adventures Network</p>
          </div>
        </div>
        <button onClick={onLogout} data-testid="mpn-logout" className="flex items-center gap-1.5 text-ink-muted hover:text-terracotta text-xs font-mono uppercase tracking-widest"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
      </div>

      <div className={`rounded-xl border border-sand-dark ${s.bg} p-6 flex items-start gap-4`} data-testid="mpn-status-card">
        <Icon className={`w-8 h-8 ${s.color} shrink-0`} />
        <div>
          <h2 className="font-display font-extrabold text-xl text-ink" data-testid="mpn-status-title">{s.title}</h2>
          <p className="text-ink-secondary mt-1">{s.msg}</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        <div className="rounded-xl bg-white border border-sand-dark p-5">
          <p className={lbl}>Your Partner ID</p>
          {partner.partner_id ? (
            <div className="flex items-center gap-2">
              <code data-testid="mpn-partner-id" className="font-display font-extrabold text-2xl text-terracotta">{partner.partner_id}</code>
              <button onClick={() => { navigator.clipboard?.writeText(partner.partner_id).then(() => toast.success("Copied")).catch(() => {}); }} className="text-moss hover:text-moss-hover"><Copy className="w-4 h-4" /></button>
            </div>
          ) : <p className="text-ink-muted text-sm">Assigned once you're approved</p>}
        </div>
        <div className="rounded-xl bg-white border border-sand-dark p-5">
          <p className={lbl}>Account</p>
          <p className="font-medium text-ink">{partner.name}</p>
          <p className="text-sm text-ink-secondary">{partner.email}</p>
        </div>
      </div>

      {!active && (
        <div className="mt-6 rounded-xl border-2 border-dashed border-sand-dark bg-sand/40 p-6 text-center" data-testid="mpn-next">
          <ShieldCheck className="w-7 h-7 text-moss mx-auto" />
          <p className="font-display font-bold text-ink mt-2">What happens next</p>
          <p className="text-sm text-ink-secondary mt-1 max-w-md mx-auto">
            Once approved you'll receive a unique Partner ID and access to approved campaigns, tracked links and your commission dashboard.
          </p>
        </div>
      )}

      {active && (
        <div className="mt-10 space-y-10">
          <CampaignLibrary />
          <LeadRegister />
          <CommissionCentre partner={partner} />
        </div>
      )}
    </div>
  );
}

function ApplyForm({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", business_name: "", country: "", phone: "", website: "", socials: "", channels: "", experience: "", payout_info: "", tax_info: "", agreement_accepted: false, standards_accepted: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async () => {
    if (!f.name || !f.email || !f.password || !f.country || !f.phone) { toast.error("Please fill in the required fields (*)"); return; }
    if (!f.agreement_accepted || !f.standards_accepted) { toast.error("Please accept the agreement and advertising standards"); return; }
    setBusy(true);
    try { const u = await applyMpn(f); toast.success("Application submitted!"); onDone(u); }
    catch (e) { toast.error(e.message || "Application failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="mpn-apply-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Full name *</label><input data-testid="mpn-name" className={field} value={f.name} onChange={set("name")} /></div>
        <div><label className={lbl}>Business name</label><input data-testid="mpn-business" className={field} value={f.business_name} onChange={set("business_name")} /></div>
        <div><label className={lbl}>Email *</label><input data-testid="mpn-email" type="email" className={field} value={f.email} onChange={set("email")} placeholder="you@example.com" /></div>
        <div><label className={lbl}>Password *</label><input data-testid="mpn-password" type="password" className={field} value={f.password} onChange={set("password")} placeholder="Min 6 characters" /></div>
        <div><label className={lbl}>Country *</label><input data-testid="mpn-country" className={field} value={f.country} onChange={set("country")} /></div>
        <div><label className={lbl}>Phone *</label><input data-testid="mpn-phone" className={field} value={f.phone} onChange={set("phone")} /></div>
        <div><label className={lbl}>Website</label><input data-testid="mpn-website" className={field} value={f.website} onChange={set("website")} placeholder="https://" /></div>
        <div><label className={lbl}>Social profiles</label><input data-testid="mpn-socials" className={field} value={f.socials} onChange={set("socials")} placeholder="@handle, links" /></div>
      </div>
      <div><label className={lbl}>Intended advertising channels</label><input data-testid="mpn-channels" className={field} value={f.channels} onChange={set("channels")} placeholder="Instagram, Facebook, TikTok, email…" /></div>
      <div><label className={lbl}>Marketing experience</label><textarea data-testid="mpn-experience" className="w-full rounded-sm bg-white border border-sand-dark text-ink text-sm px-3 py-2 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-terracotta" value={f.experience} onChange={set("experience")} /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Payout details</label><input data-testid="mpn-payout" className={field} value={f.payout_info} onChange={set("payout_info")} placeholder="Bank / PayPal (for approved partners)" /></div>
        <div><label className={lbl}>Tax info</label><input data-testid="mpn-tax" className={field} value={f.tax_info} onChange={set("tax_info")} placeholder="ABN / Tax ID (if applicable)" /></div>
      </div>
      <label className="flex items-start gap-2.5 text-sm text-ink-secondary"><input type="checkbox" data-testid="mpn-agree" checked={f.agreement_accepted} onChange={set("agreement_accepted")} className="mt-0.5 accent-terracotta" /> I accept the Marketing Partner Network Agreement.</label>
      <label className="flex items-start gap-2.5 text-sm text-ink-secondary"><input type="checkbox" data-testid="mpn-standards" checked={f.standards_accepted} onChange={set("standards_accepted")} className="mt-0.5 accent-terracotta" /> I accept the advertising standards (no changing prices/offers, never present as Responsible Adventures).</label>
      <button onClick={submit} disabled={busy} data-testid="mpn-apply-btn" className="w-full h-12 rounded-sm bg-terracotta hover:bg-terracotta-hover text-white font-semibold tracking-widest uppercase text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Apply to join"}
      </button>
    </div>
  );
}

function SignIn({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "mpn") { clearToken(); toast.error("This isn't a Marketing Partner account"); return; }
      onDone(u);
    } catch (e) { toast.error(e.message || "Login failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="mpn-signin-form">
      <div><label className={lbl}>Email</label><input data-testid="mpn-login-email" type="email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      <div><label className={lbl}>Password</label><input data-testid="mpn-login-password" type="password" className={field} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
      <button onClick={submit} disabled={busy} data-testid="mpn-login-btn" className="w-full h-12 rounded-sm bg-ink hover:opacity-90 text-white font-semibold tracking-widest uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
      </button>
    </div>
  );
}

export default function MarketingPartner() {
  const [partner, setPartner] = useState(null);
  const [tab, setTab] = useState("apply");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (isMpn()) {
      fetchMpnMe().then(setPartner).catch(() => { clearToken(); }).finally(() => setReady(true));
    } else { setReady(true); }
  }, []);

  const logout = () => { clearToken(); setPartner(null); };

  if (!ready) return <div className="min-h-screen bg-base flex items-center justify-center text-ink-secondary"><Loader2 className="w-6 h-6 animate-spin text-terracotta mr-2" /> Loading…</div>;
  if (partner) return <div className="min-h-screen bg-base"><Dashboard partner={partner} onLogout={logout} /></div>;

  return (
    <div className="min-h-screen bg-base grain" data-testid="mpn-page">
      <div className="max-w-xl mx-auto px-5 py-12">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-terracotta rounded-sm flex items-center justify-center mx-auto mb-3"><Megaphone className="w-6 h-6 text-white" /></div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Marketing Partner Network</h1>
          <p className="text-ink-secondary mt-2">Promote approved Responsible Adventures campaigns and earn a protected <b>15% commission</b> when your leads convert. You generate interest — we manage the traveller and the payments.</p>
        </div>
        <div className="flex rounded-sm border border-sand-dark overflow-hidden mb-6">
          <button data-testid="mpn-tab-apply" onClick={() => setTab("apply")} className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${tab === "apply" ? "bg-terracotta text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Apply</button>
          <button data-testid="mpn-tab-signin" onClick={() => setTab("signin")} className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${tab === "signin" ? "bg-terracotta text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Partner sign in</button>
        </div>
        <div className="rounded-xl bg-white border border-sand-dark p-6">
          {tab === "apply" ? <ApplyForm onDone={setPartner} /> : <SignIn onDone={setPartner} />}
        </div>
      </div>
    </div>
  );
}
